import { Plus, Inbox as InboxIcon, Pencil, MessageSquare, Folders } from 'lucide-react'
import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Tooltip } from '@/components/ui/Tooltip'
import DeleteButton from '@/components/ui/DeleteButton'
import { InboxFormDialog, type InboxFormData } from './InboxFormDialog'
import { useRouteParams } from '@/hooks/useRoutes'

interface Inbox {
  id: string
  name: string
  slug: string
  active: boolean
  conversations_count: number
  topics_count: number
  follow_up_enabled: boolean
  follow_up_delay_minutes: number
}

interface InboxesData {
  inboxes: Inbox[]
  max_inboxes: number | null
  can_create: boolean
}

interface InboxesSettingsCardProps {
  inboxes: InboxesData | null
}

export function InboxesSettingsCard({ inboxes: inboxesData }: InboxesSettingsCardProps) {
  const { withAccount } = useRouteParams()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingInbox, setEditingInbox] = useState<Inbox | null>(null)
  const [formData, setFormData] = useState<InboxFormData>({
    name: '',
    active: true,
    follow_up_enabled: true,
    follow_up_delay_minutes: 3
  })

  const accountParams = withAccount()

  if (!inboxesData) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">Unable to load inboxes data.</p>
        </CardContent>
      </Card>
    )
  }

  const { inboxes, max_inboxes, can_create } = inboxesData
  const unlimited = max_inboxes === null || max_inboxes === -1
  const limitDisplay = unlimited ? '∞' : max_inboxes

  const openCreateDialog = () => {
    setEditingInbox(null)
    setFormData({ name: '', active: true, follow_up_enabled: true, follow_up_delay_minutes: 3 })
    setDialogOpen(true)
  }

  const openEditDialog = (inbox: Inbox) => {
    setEditingInbox(inbox)
    setFormData({
      name: inbox.name,
      active: inbox.active,
      follow_up_enabled: inbox.follow_up_enabled,
      follow_up_delay_minutes: inbox.follow_up_delay_minutes,
    })
    setDialogOpen(true)
  }

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      return
    }

    if (editingInbox) {
      router.patch(
        `/${accountParams.account_id}/inboxes/${editingInbox.id}`,
        {
          inbox: {
            name: formData.name.trim(),
            active: formData.active,
            settings: {
              follow_up_enabled: formData.follow_up_enabled,
              follow_up_delay_minutes: formData.follow_up_delay_minutes,
            },
          },
        },
        {
          preserveScroll: true,
          onSuccess: () => setDialogOpen(false),
        }
      )
    } else {
      router.post(
        `/${accountParams.account_id}/inboxes`,
        { inbox: { name: formData.name.trim(), active: formData.active } },
        {
          preserveScroll: true,
          onSuccess: () => setDialogOpen(false),
        }
      )
    }
  }

  const handleDelete = (inboxId: string) => {
    router.delete(`/${accountParams.account_id}/inboxes/${inboxId}`, {
      preserveScroll: true,
    })
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <InboxIcon className="size-5" />
              Inboxes
            </CardTitle>
            <CardDescription>
              Manage your inboxes. Topics route conversations to specific inboxes.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {inboxes.length} / {limitDisplay}
            </span>
            {can_create ? (
              <Button
                onClick={openCreateDialog}
                size="sm"
                className="cursor-pointer"
              >
                <Plus className="size-4 mr-1" />
                Add Inbox
              </Button>
            ) : (
              <Tooltip content={`Upgrade to create more inboxes (limit: ${limitDisplay})`}>
                <Button
                  size="sm"
                  className="cursor-pointer"
                  disabled
                >
                  <Plus className="size-4 mr-1" />
                  Add Inbox
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {inboxes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No inboxes found. This shouldn't happen - contact support.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {inboxes.map((inbox) => (
              <div
                key={inbox.id}
                className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg group"
              >
                <InboxIcon className="size-5 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{inbox.name}</span>
                    {!inbox.active && (
                      <span className="text-xs bg-muted px-2 py-0.5 rounded">Inactive</span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <MessageSquare className="size-3" />
                      {inbox.conversations_count} conversations
                    </span>
                    <span className="flex items-center gap-1">
                      <Folders className="size-3" />
                      {inbox.topics_count} topics
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                  <Tooltip content="Edit inbox">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(inbox)}
                      className="cursor-pointer"
                    >
                      <Pencil className="size-4" />
                    </Button>
                  </Tooltip>
                  {inboxes.length > 1 && (
                    <DeleteButton
                      onDelete={() => handleDelete(inbox.id)}
                      initialText=""
                      confirmText="Delete?"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!can_create && (
          <p className="text-sm text-muted-foreground mt-4">
            You've reached your inbox limit ({limitDisplay}). Upgrade your plan to create more inboxes.
          </p>
        )}
      </CardContent>

      <InboxFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        formData={formData}
        onFormChange={setFormData}
        onSubmit={handleSubmit}
        isEditing={!!editingInbox}
      />
    </Card>
  )
}
